import * as React from 'react'

import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { dateBackendDateTimeToUIUtcTime } from 'modules/core/lib/dates'
import {
  firstDayOfWeekAsKey,
  weekdaysWithUserFirstDayOfWeek,
} from 'modules/core/lib/firstDayOfWeek'
import { formatSecondsToDayInputForm } from 'modules/core/lib/formatTime'

import PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { totalDurationFromUnpaidBreakForms } from 'modules/shift_management/lib/totalDurationFromUnpaidBreaks'
import { useBulkCudPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useBulkCudPlannedBreaks'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'
import DayConfigurationForm from 'modules/work_schedules/lib/forms/DayConfigurationForm'
import dayConfigurationsCollection, {
  DayConfigurations,
} from 'modules/work_schedules/store/collections/dayConfigurations'
import type DayConfiguration from 'modules/work_schedules/store/models/DayConfiguration'

export interface DayConfigurationWithBreaksForm {
  dayConfiguration: DayConfigurationForm
  breaks: PlannedBreakForm[]
}

const useDayConfigurations = (dayConfigurations: DayConfiguration[], scheduleType: string) => {
  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()
  const { bulkCud } = useBulkCudPlannedBreaks()
  const { plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { dayConfigurationIds: dayConfigurations.map((c) => c.id) },
    skip: !dayConfigurations.length,
  })

  useEffect(() => {
    setDayConfigForms(createDayConfigForms())
  }, [plannedBreaksCollection.plannedBreaks.length])

  const getBreakForms = (configurationId: number): PlannedBreakForm[] => {
    const collection = plannedBreaksCollection.filterByDayConfigurations([configurationId])
    return collection.map((item) => {
      const { id, startAt, endAt, breakType, duration, breakConfiguration } = item

      return new PlannedBreakForm({
        id,
        start_at: startAt && dateBackendDateTimeToUIUtcTime(startAt),
        end_at: endAt && dateBackendDateTimeToUIUtcTime(endAt),
        break_type: breakType,
        duration: duration,
        'break_configuration.id': breakConfiguration?.id,
        'break_configuration.name': breakConfiguration?.name,
        'break_configuration.paid': breakConfiguration?.paid,
        'break_configuration.archived': breakConfiguration?.archived,
      })
    })
  }

  const createDayConfigForms = () => {
    return weekdaysWithUserFirstDayOfWeek(firstDayOfWeek).flatMap((weekday: string) => {
      const dayConfigsByDay = groupedByDay[weekday]

      if (!dayConfigsByDay) return blankDayConfigFormWithBreaks(weekday)

      return dayConfigsByDay.map((dayConfig) => {
        const breaks: PlannedBreakForm[] = getBreakForms(dayConfig.id)
        return dayConfigFormWithBreaks(weekday, dayConfig, breaks)
      })
    })
  }

  const blankDayConfigFormWithBreaks = (weekday: string) => {
    const dayConfiguration = new DayConfigurationForm({
      weekday,
      duration_in_seconds: 0,
      schedule_type: scheduleType,
    })

    return {
      dayConfiguration,
      breaks: [],
    }
  }

  const dayConfigFormWithBreaks = (
    weekday: string,
    dayConfig: DayConfiguration,
    breaks: PlannedBreakForm[]
  ) => {
    const dayConfiguration = new DayConfigurationForm({
      id: dayConfig.attributes.id,
      weekday: weekday,
      start_at: dayConfig.attributes.start_at,
      end_at: dayConfig.attributes.end_at,
      duration_in_seconds: dayConfig.attributes.duration_in_seconds,
      duration_in_hours: formatSecondsToDayInputForm(dayConfig.attributes.duration_in_seconds),
      schedule_type: scheduleType,
    })

    return {
      dayConfiguration,
      breaks,
    }
  }

  const groupedByDay = groupBy(
    dayConfigurations,
    (dayConfiguration: DayConfiguration) => dayConfiguration.attributes.weekday
  )

  const [dayConfigForms, setDayConfigForms] = React.useState<DayConfigurationWithBreaksForm[]>(
    createDayConfigForms()
  )

  const totalHours = (): number => {
    const dayConfigs = dayConfigForms.filter((config) => config.dayConfiguration.isValid)

    if (!dayConfigs) return 0

    const secondsPerDayConfig = dayConfigs.map(({ dayConfiguration, breaks }) => {
      // We need to subtract the time from unpaid breaks
      const totalBreaks = totalDurationFromUnpaidBreakForms(breaks)

      return dayConfiguration.duration - totalBreaks
    })

    return secondsPerDayConfig.reduce((acc: number, curr: number) => acc + curr, 0)
  }

  const onAddDayConfig = (weekday: string) => {
    const newDayConfigForm = blankDayConfigFormWithBreaks(weekday)
    setDayConfigForms([...dayConfigForms, newDayConfigForm])
  }

  const onDeleteDayConfig = (dayConfigForm: DayConfigurationForm) => {
    const weekday = dayConfigForm.get('weekday').value

    const dayConfigsByDay = dayConfigForms.filter(
      (form) => form.dayConfiguration.get('weekday').value === weekday
    )

    if (dayConfigsByDay.length > 1) {
      const updatedForms = dayConfigForms.filter((form) => form.dayConfiguration !== dayConfigForm)
      setDayConfigForms(updatedForms)
    } else {
      dayConfigForm.get('id').set('')
      dayConfigForm.get('start_at').set('')
      dayConfigForm.get('end_at').set('')
      dayConfigForm.cleanAll()
    }
  }

  const onCopyFirstToAll = () => {
    const weekDays = weekdaysWithUserFirstDayOfWeek(firstDayOfWeek)
    const firstDay = firstDayOfWeekAsKey(firstDayOfWeek)

    if (!firstDay) {
      return
    }

    const firstDayConfigs = groupBy(
      dayConfigForms,
      (form) => form.dayConfiguration.get('weekday').value
    )[firstDay]

    if (!firstDayConfigs) return

    const newConfigs = weekDays.flatMap((weekday: string) => {
      return firstDayConfigs.map(({ dayConfiguration, breaks }) => {
        return {
          dayConfiguration: dayConfiguration.clone(weekday),
          breaks: breaks.map((b) => b.clone()),
        }
      })
    })

    setDayConfigForms(newConfigs)
  }

  const saveDayConfigurations = async (periodId: number) => {
    const dayConfigurations = dayConfigForms
      .filter((form) => form.dayConfiguration.isValid)
      .map((dayConfigForm) => ({
        ...dayConfigForm.dayConfiguration.data(),
        duration_in_seconds: dayConfigForm.dayConfiguration.duration,
      }))

    const result = await asyncAction(() =>
      dayConfigurationsCollection.rpc('bulk_cud', {
        overlap_period_id: periodId,
        day_configurations: dayConfigurations,
      })
    )

    await dayConfigurationsCollection.fetch()

    if (isSuccessful(result)) {
      const dayConfigs: DayConfiguration[] = new DayConfigurations(result.value).models
      const dayConfigIds = new Set(dayConfigs.map((config) => +config.id))

      // When day configurations are created, we assign the corresponding id to its form object.
      // This is necessary to send the right id for any break associated to that day configuration,
      // which is required to create said break.
      //
      dayConfigForms.forEach(({ dayConfiguration }) => {
        dayConfigs.forEach((config) => {
          if (dayConfiguration.isMatchWith(config)) {
            dayConfiguration.setValues({ id: config.id })
          }
        })
      })

      const plannedBreaksParams = dayConfigForms
        .filter(({ dayConfiguration }) => dayConfigIds.has(+dayConfiguration.id))
        .map(({ dayConfiguration, breaks }) => ({
          dayConfigurationId: +dayConfiguration.get('id').value,
          plannedBreaks: breaks.map((plannedBreak) => ({
            startAt: plannedBreak.formattedStartAt,
            endAt: plannedBreak.formattedEndAt,
            duration: +plannedBreak.get('duration').value,
            breakType: plannedBreak.get('break_type').value,
            breakConfigurationId: +plannedBreak.get('break_configuration.id').value,
          })),
        }))

      bulkCud({ plannedBreaksParams })
    }
  }

  const onAddBreak = (
    breakConfiguration: BreakConfigurationFragment,
    breakType: BreakType,
    dayConfiguration: DayConfigurationWithBreaksForm
  ) => {
    const { name, id, paid } = breakConfiguration
    const form = new PlannedBreakForm({
      'break_configuration.id': id,
      'break_configuration.name': name,
      'break_configuration.paid': paid,
      break_type: breakType,
    })

    track(trackingEvents.shiftManagement.addBreakSubmit, { from: 'work-schedules' })

    const newConfigs = dayConfigForms.map((dayConfigForm: DayConfigurationWithBreaksForm) => {
      if (dayConfigForm === dayConfiguration) dayConfigForm.breaks.push(form)
      return dayConfigForm
    })

    setDayConfigForms(newConfigs)
  }

  const onDeleteBreak = (
    dayConfigurationForm: DayConfigurationWithBreaksForm,
    form: PlannedBreakForm
  ) => {
    const { dayConfiguration } = dayConfigurationForm
    const config = dayConfigForms.find((form) => form.dayConfiguration === dayConfiguration)

    if (!config) return

    const newConfigs = dayConfigForms.map((dayConfigForm) => {
      if (dayConfigForm.dayConfiguration === dayConfiguration) {
        dayConfigForm.breaks = dayConfigForm.breaks.filter((breakForm) => breakForm !== form)
      }
      return dayConfigForm
    })

    setDayConfigForms(newConfigs)
  }

  return {
    dayConfigForms,
    totalHours: totalHours(),
    onAddDayConfig,
    onDeleteDayConfig,
    onCopyFirstToAll,
    onAddBreak,
    onDeleteBreak,
    saveDayConfigurations,
  }
}

export default useDayConfigurations
